import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCRMStore } from '@/stores/useCRMStore';
import { X, Phone, Mail, FileText, CheckSquare } from 'lucide-react';

export default function CaseDetailModal() {
  const { selectedCase, setSelectedCase } = useCRMStore();

  if (!selectedCase) return null;

  return (
    <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div>
              <span className="text-xl font-bold">
                Case: ...{String(selectedCase.account_id).slice(-12)}
              </span>
              <Badge className="ml-3" variant="outline">{selectedCase.stage}</Badge>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSelectedCase(null)}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Account ID</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold font-mono">{selectedCase.account_id}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant="outline" className="text-lg">{selectedCase.stage}</Badge>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contact">Contact Log</TabsTrigger>
            <TabsTrigger value="arrangement">Arrangement</TabsTrigger>
            <TabsTrigger value="qc">QC</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Case Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Stage:</span>
                  <span className="font-medium">{selectedCase.stage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Negative Outcomes:</span>
                  <span className="font-medium">{selectedCase.neg_outcomes_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Next Action:</span>
                  <span className="font-medium">
                    {selectedCase.next_action_at 
                      ? new Date(selectedCase.next_action_at).toLocaleDateString()
                      : 'Not set'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm">
                  <Phone className="h-4 w-4 mr-2" />
                  Log Call
                </Button>
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
                <Button variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  Create Arrangement
                </Button>
                <Button variant="outline" size="sm">
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Start QC
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contact History</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No contact records yet</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="arrangement">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Payment Arrangements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No arrangements yet</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="qc">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quality Control</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">QC not started</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No notes yet</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
